import { RolesFindAllPayload, RolesService } from "../services/roles.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useUserStore } from "./useUserStore";
import {
  RoleTypeEnum,
  StandardAccountRoleSubtypeEnum,
} from "@/domain/roles.enum";
import { RolePayload } from "@/domain/rol.interface";

export const useRoles = () => {
  const { userInfo } = useUserStore();
  const userRole = userInfo?.roles[0] as unknown as string;

  const { data: roles, isLoading: isLoadingRoles } = useQuery({
    queryKey: ["roles"],
    queryFn: () => RolesService.findAll({ status: "ACTIVE" }),
    select: (data) => {
      if (userRole) {
        if (
          data.some((role) => role.name === "Sysadmin" && role._id === userRole)
        ) {
          return data;
        } else if (
          data.some(
            (role) =>
              (role.name === "Support" || role.name === "Account Admin") &&
              role._id === userRole
          )
        ) {
          data = data.filter((role) => role.name !== "Sysadmin");
        } else {
          data = data.filter((role) => role.type === "STANDARD_SYSTEM_ROLE");
        }
      }
      return data;
    },
  });

  return {
    roles,
    isLoadingRoles,
  };
};

export const useOfficerRoles = () => {
  const { data: officerRoles, isLoading: isLoadingOfficerRoles } = useQuery({
    queryKey: ["officerRoles"],
    queryFn: () => RolesService.findAll({ status: "ACTIVE" }),
    select: (data) => {
      return data.filter(
        (role) =>
          role.type === RoleTypeEnum.STANDARD_ACCOUNT_ROLE &&
          role.subType === StandardAccountRoleSubtypeEnum.OFFICER
      );
    },
  });

  return {
    officerRoles,
    isLoadingOfficerRoles,
  };
};

export const useRolesPaginated = (filters: RolesFindAllPayload) => {
  const {
    data: rolesPaginated,
    isLoading: isLoadingRolesPaginated,
    isFetching: isFetchingRolesPaginated,
    refetch: refetchRolesPaginated,
  } = useQuery({
    queryKey: ["parca-roles-list"],
    queryFn: () => RolesService.findAllPaginated(filters),
  });

  return {
    rolesPaginated,
    isLoadingRolesPaginated,
    isFetchingRolesPaginated,
    refetchRolesPaginated,
  };
};

export const useCreateRole = () => {
  return useMutation({
    mutationFn: (payload: RolePayload) => RolesService.createOne(payload),
  });
};

export const useUpdateRole = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RolePayload }) =>
      RolesService.updateOne({ id, payload }),
  });
};

export const useArchiveRole = () => {
  return useMutation({
    mutationFn: (id: string) => RolesService.archiveOne(id),
  });
};
